import type { ReactNode } from 'react';
import {
  Alert,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type { CredentialingStatus, PublicCredentialingStatus } from '../../types';

import { CREDENTIALING_PAGE_SIZE, CREDENTIALING_STATUS_LABELS } from './credentialingStatus';

export function CredentialingStatusSelect({
  value,
  onChange,
  disabled,
  label = 'Status',
}: {
  value: CredentialingStatus;
  onChange: (value: CredentialingStatus) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <TextField
      select
      size="small"
      label="Status"
      slotProps={{
        select: { SelectDisplayProps: { 'aria-label': label, 'aria-labelledby': undefined } },
      }}
      value={value}
      disabled={disabled}
      onChange={event => {
        const status = event.target.value;
        if (status === 'not_started' || status === 'in_progress' || status === 'complete')
          onChange(status);
      }}
      sx={{ minWidth: 150 }}
    >
      {Object.entries(CREDENTIALING_STATUS_LABELS).map(([status, text]) => (
        <MenuItem key={status} value={status}>
          {text}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Only public fields are rendered, even when the staff response contains metadata. */
export function CredentialingStatusPanel({
  rows,
  pending,
  failed,
  offset,
  onPage,
  onRetry,
  providerLabel,
  payerLabel,
  onUpdate,
  saving = false,
  children,
}: {
  rows: PublicCredentialingStatus[];
  pending: boolean;
  failed: boolean;
  offset: number;
  onPage: (offset: number) => void;
  onRetry: () => void;
  providerLabel?: (id: string) => ReactNode;
  payerLabel?: (id: string) => ReactNode;
  onUpdate?: (row: PublicCredentialingStatus, status: CredentialingStatus) => void;
  saving?: boolean;
  children?: ReactNode;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6" component="h2">
        Credentialing
      </Typography>
      <Typography color="text.secondary">
        Status for each provider and payer, updated by JotPsych. These records do not indicate
        completion for the whole practice.
      </Typography>
      {children}
      {pending ? (
        <LinearProgress aria-label="Loading credentialing statuses" />
      ) : failed ? (
        <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
          We couldn’t load credentialing statuses.
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          {offset === 0
            ? 'No credentialing statuses have been added yet.'
            : 'No more credentialing statuses.'}
        </Alert>
      ) : (
        <TableContainer>
          <Table aria-label="Credentialing statuses">
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Payer</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {providerLabel?.(row.user_id) ?? row.user_id}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {payerLabel?.(row.payer_uuid) ?? row.payer_uuid}
                  </TableCell>
                  <TableCell>
                    {onUpdate ? (
                      <CredentialingStatusSelect
                        label={`Status for ${row.user_id} / ${row.payer_uuid}`}
                        value={row.status}
                        disabled={saving}
                        onChange={status => onUpdate(row, status)}
                      />
                    ) : (
                      <Chip
                        label={CREDENTIALING_STATUS_LABELS[row.status]}
                        color={
                          row.status === 'complete'
                            ? 'success'
                            : row.status === 'in_progress'
                              ? 'info'
                              : 'default'
                        }
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          disabled={offset === 0 || pending || saving}
          onClick={() => onPage(Math.max(0, offset - CREDENTIALING_PAGE_SIZE))}
        >
          Previous
        </Button>
        <Typography variant="body2">Page {offset / CREDENTIALING_PAGE_SIZE + 1}</Typography>
        <Button
          disabled={pending || failed || saving || rows.length < CREDENTIALING_PAGE_SIZE}
          onClick={() => onPage(offset + CREDENTIALING_PAGE_SIZE)}
        >
          Next
        </Button>
        <Button disabled={pending || saving} onClick={onRetry}>
          Refresh
        </Button>
      </Stack>
    </Stack>
  );
}
